import { useCallback, useEffect, useState } from "react";

/**
 * Problems a homeowner listed but has not filed yet.
 *
 * Someone writes "1) tap leaks 2) door sticks 3) socket is dead". The assistant drafts a case
 * for the first and returns the rest as `otherIssues`. They wait here and are offered one by
 * one after each submit, so nothing is retyped and nothing is lost.
 *
 *   draft: tap leaks        queue: [door sticks, socket is dead]
 *   submit ──► next: door sticks   queue: [socket is dead]
 *
 * Kept in sessionStorage per household: a reload keeps the list, a new browser session starts clean.
 */

const storageKey = (householdId: number) => `warranty-issue-queue:${householdId}`;
const PHONE_KEY = "warranty-contact-phone";

/** Joins issue lists in order, dropping blanks and repeats. */
export const mergeIssues = (...lists: (string[] | null | undefined)[]): string[] => {
  const seen = new Set<string>();
  return lists
    .flatMap((list) => list ?? [])
    .map((issue) => issue.trim())
    .filter((issue) => {
      const key = issue.toLowerCase();
      if (!issue || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
};

const readIssues = (householdId: number): string[] => {
  try {
    const stored: unknown = JSON.parse(sessionStorage.getItem(storageKey(householdId)) ?? "[]");
    return Array.isArray(stored) ? mergeIssues(stored.filter((item) => typeof item === "string")) : [];
  } catch {
    return [];
  }
};

const writeIssues = (householdId: number, issues: string[]) => {
  try {
    if (issues.length > 0) {
      sessionStorage.setItem(storageKey(householdId), JSON.stringify(issues));
    } else {
      sessionStorage.removeItem(storageKey(householdId));
    }
  } catch {
    // Storage can be unavailable (private mode); the queue then lives in memory only.
  }
};

export const useIssueQueue = (householdId: number) => {
  const [issues, setIssues] = useState<string[]>(() => readIssues(householdId));

  useEffect(() => {
    setIssues(readIssues(householdId));
  }, [householdId]);

  const replace = useCallback(
    (next: string[]) => {
      const merged = mergeIssues(next);
      writeIssues(householdId, merged);
      setIssues(merged);
    },
    [householdId],
  );

  return [issues, replace] as const;
};

/** The contact phone typed for the previous case, so a batch of cases does not ask for it each time. */
export const rememberedPhone = (): string => {
  try {
    return sessionStorage.getItem(PHONE_KEY) ?? "";
  } catch {
    return "";
  }
};

export const rememberPhone = (phone: string) => {
  try {
    sessionStorage.setItem(PHONE_KEY, phone);
  } catch {
    // See writeIssues.
  }
};
